import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { gsap, registerGsap } from '../anim/gsapSetup';
import { Seo } from '../components/Seo';
import { Eyebrow } from '../components/common/Eyebrow';
import { SplitHeading } from '../components/animation/SplitHeading';
import { Reveal } from '../components/animation/Reveal';
import { Button } from '../components/common/Button';
import { Icon } from '../components/common/Icon';
import { LeafMark } from '../components/common/Logo';
import { AccentGlow } from '../components/common/AccentGlow';
import { LoadingScreen } from '../components/common/Spinner';
import { ProductImage } from '../components/product/ProductImage';
import { VariantSelector } from '../components/product/VariantSelector';
import { Accordion } from '../components/common/Accordion';
import { sectionsToAccordionItems } from '../components/product/ProductSections';
import { RelatedProducts } from '../components/product/RelatedProducts';
import { productRepository } from '../services/productRepository';
import { categoryBySlug } from '../lib/categories';
import { company, whatsappLink } from '../data/company';
import { productImageAlt } from '../lib/image';
import { accentAlpha, onLight } from '../lib/slug';
import { productSchema, breadcrumbSchema, faqSchema } from '../lib/seo';
import { extractProductFacts } from '../lib/productFacts';
import { productFaq } from '../lib/productFaq';
import { cropPagePath, cropProgrammesForProduct } from '../lib/cropLinks';
import { articlesForProduct } from '../data/knowledge';
import { ProductGlance, ApprovalPanel, DocumentsPanel, FaqBlock } from '../components/product/ProductFactPanels';
import { DosageTable } from '../components/product/DosageTable';
import { SpecTable } from '../components/product/SpecTable';
import { useReducedMotion } from '../anim/useReducedMotion';
import { useProduct } from '../hooks';

const FORM_LABEL: Record<string, string> = { liquid: 'Liquid', powder: 'Powder', granule: 'Granule' };

export default function ProductDetailsPage() {
  const { slug = '' } = useParams();
  const { data: product, isLoading } = useProduct(slug);
  const [activeVariantId, setActiveVariantId] = useState('');
  const reduced = useReducedMotion();
  const imgWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (product) setActiveVariantId(product.variants.find((v) => v.image)?.id ?? product.variants[0]?.id ?? '');
  }, [product]);

  useLayoutEffect(() => {
    if (reduced || !imgWrapRef.current || !activeVariantId) return;
    registerGsap();
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '[data-detail="img"]',
        { opacity: 0.35, scale: 0.94, filter: 'blur(6px)' },
        { opacity: 1, scale: 1, filter: 'blur(0px)', duration: 0.5, ease: 'power3.out' },
      );
    }, imgWrapRef);
    return () => ctx.revert();
  }, [activeVariantId, reduced]);

  if (isLoading) {
    return (
      <div className="pt-[var(--nav-h)]">
        <LoadingScreen label="Loading product" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="grain flex min-h-[70vh] flex-col items-center justify-center bg-night px-6 text-center text-white">
        <LeafMark className="mb-6 h-14 w-14 text-brand-400" />
        <h1 className="font-display text-h1 font-semibold">Product not found</h1>
        <p className="mt-3 max-w-md text-white/70">We couldn’t find that product. It may have been renamed or removed.</p>
        <Button to="/catalog" variant="primary" size="lg" className="mt-7">
          <Icon name="ArrowLeft" className="h-5 w-5" /> All products
        </Button>
      </div>
    );
  }

  const accent = product.accentColor;
  const category = categoryBySlug(product.categorySlug);
  const activeVariant = product.variants.find((v) => v.id === activeVariantId) ?? product.variants[0];
  const related = productRepository.getRelatedProducts(product);
  // The catalogue's untyped detail sections, read once into named facts. Everything below — the
  // glance list, the composition/specification/dose tables, the FAQ, the Product JSON-LD and the
  // Markdown mirror — is that same reading, so the page and the machine-readable copy cannot disagree.
  const facts = extractProductFacts(product);
  const faq = productFaq(product, facts);
  const cropProgrammes = cropProgrammesForProduct(product);
  const articles = articlesForProduct(product.slug, product.categorySlug);
  // Only the sections the extractor could NOT place keep the generic accordion. An empty authored
  // section ("Table", two unnamed columns, no rows) is dropped rather than rendered as a blank panel.
  const detailItems = sectionsToAccordionItems(facts.other);
  const crumbs = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/catalog' },
    ...(category ? [{ name: category.name, path: `/product-categories/${category.slug}` }] : []),
    { name: product.name, path: `/catalog/${product.slug}` },
  ];

  return (
    <div style={{ ['--accent' as string]: accent }}>
      <Seo
        title={product.seo.title}
        description={product.seo.description}
        path={`/catalog/${product.slug}`}
        type="product"
        image={activeVariant?.image ?? undefined}
        keywords={product.seo.keywords}
        markdownPath={`/catalog/${product.slug}.md`}
        jsonLd={[
          productSchema(product, facts, category),
          breadcrumbSchema(crumbs),
          ...(faq.length ? [faqSchema(faq)] : []),
        ]}
      />

      <header
        className="grain relative overflow-hidden bg-night pb-8 pt-[calc(var(--nav-h)+2.5rem)] text-white"
        style={{ ['--leaf-vein' as string]: '#06231A' }}
      >
        <AccentGlow color={accent} intensity={0.45} className="right-[-6%] top-[-30%] h-[32rem] w-[32rem]" />
        <div className="container-x relative">
          {category?.name && (
            <Eyebrow tone="light" style={{ color: accent }}>
              {category.name}
            </Eyebrow>
          )}
          <SplitHeading as="h1" text={product.name} type="words" className="mt-3 font-display text-display-xl font-semibold text-white" start="top 95%" />
          {product.tagline && <p className="mt-3 max-w-2xl text-lead text-white/75">{product.tagline}</p>}
        </div>
      </header>

      {/* `grid-cols-1` is load-bearing below `lg`: with no explicit track the implicit column is `auto`
          and grew to the bottle image's intrinsic width, so the product page scrolled sideways on every
          phone (measured 242px at 320, 202 at 360, 172 at 390, 133 at 430). */}
      <div className="container-x grid grid-cols-1 gap-10 py-14 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-16">
        {/* left: sticky bottle + buy box */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div
            ref={imgWrapRef}
            className="relative overflow-hidden rounded-2xl border border-sand-200"
            style={{ background: `radial-gradient(120% 90% at 50% 8%, ${accentAlpha(accent, 0.16)}, #ffffff 66%)` }}
          >
            <AccentGlow color={accent} intensity={0.35} className="left-1/2 top-4 h-64 w-64 -translate-x-1/2" />
            <div data-detail="img" className="relative aspect-square p-8">
              <ProductImage
                key={activeVariant?.id}
                src={activeVariant?.image ?? null}
                alt={productImageAlt(product, activeVariant)}
                accent={accent}
                name={product.name}
                priority
                className="h-full w-full"
                sizes="(max-width: 1024px) 90vw, 460px"
              />
            </div>
          </div>

          <div className="mt-6">
            <p className="mb-2 text-sm font-semibold text-ink-700">Available pack sizes</p>
            <VariantSelector variants={product.variants} activeId={activeVariantId} onChange={setActiveVariantId} accent={accent} />
          </div>

          <dl className="mt-6 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg border border-sand-200 bg-white p-3">
              <dt className="text-xs uppercase tracking-wide text-ink-500">Type</dt>
              <dd className="mt-0.5 font-semibold text-ink">{FORM_LABEL[product.form] ?? 'Product'}</dd>
            </div>
            {category?.name && (
              <div className="rounded-lg border border-sand-200 bg-white p-3">
                <dt className="text-xs uppercase tracking-wide text-ink-500">Category</dt>
                <dd className="mt-0.5 font-semibold text-ink">{category.name}</dd>
              </div>
            )}
            {activeVariant && (
              <div className="rounded-lg border border-sand-200 bg-white p-3">
                <dt className="text-xs uppercase tracking-wide text-ink-500">Selected</dt>
                <dd className="mt-0.5 font-semibold text-ink">{activeVariant.packSize}</dd>
              </div>
            )}
            {/* Price is intentionally NOT shown on the public site (or to farmers) — enquire for pricing. */}
          </dl>

          <div className="mt-6 flex flex-col gap-3">
            <Button to={`/inquiry/${product.id}`} variant="accent" size="lg" className="w-full">
              <Icon name="Send" className="h-5 w-5" /> Enquire about {product.name}
            </Button>
            <div className="grid grid-cols-2 gap-3">
              <a
                href={whatsappLink(`Hi GreenbayBio, I'd like to know more about ${product.name}.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline btn-md"
              >
                <Icon name="MessageCircle" className="h-4 w-4" /> WhatsApp
              </a>
              <a href={`tel:${company.phoneHref}`} className="btn btn-outline btn-md">
                <Icon name="Phone" className="h-4 w-4" /> Call us
              </a>
            </div>
          </div>

          {/* Downloadable datasheets — the multi-language catalogue files (/catalogues) plus anything
              attached in the dashboard. The PDF stays the primary document; this page states the same
              facts in html so a reader (or a crawler) never has to open it to learn what the product is. */}
          <DocumentsPanel product={product} compact />
        </div>

        {/* right: details */}
        <div>
          <ProductGlance product={product} facts={facts} category={category} cropPageFor={cropPagePath} />

          {product.fullDescription.length > 0 && (
            <Reveal className="space-y-4 text-lg leading-relaxed text-ink-700" stagger={0.1} y={20}>
              {product.fullDescription.map((para, i) => (
                <p key={i} className={i === 0 ? 'text-xl text-ink' : ''}>
                  {para}
                </p>
              ))}
            </Reveal>
          )}

          {product.benefits.length > 0 && (
            <div className="mt-12">
              <h2 className="font-display text-h3 font-semibold text-ink" style={{ color: onLight(accent) }}>
                Key benefits
              </h2>
              <Reveal as="ul" className="mt-5 grid gap-3 sm:grid-cols-2" stagger={0.08} y={18}>
                {product.benefits.map((b) => (
                  <li key={b} className="flex gap-3 rounded-lg border border-sand-200 bg-white p-4 shadow-soft">
                    <Icon name="CheckCircle2" className="mt-0.5 h-5 w-5 shrink-0" style={{ color: accent }} />
                    <span className="text-[0.95rem] leading-relaxed text-ink-700">{b}</span>
                  </li>
                ))}
              </Reveal>
            </div>
          )}

          {facts.compositionRows.length > 0 && (
            <section className="mt-12" aria-labelledby="composition-heading">
              <h2 id="composition-heading" className="font-display text-h3 font-semibold text-ink">
                Composition
              </h2>
              {facts.compositionSummary && <p className="mt-2 text-ink-600">{facts.compositionSummary}</p>}
              <div className="mt-4 rounded-xl border border-sand-200 bg-white px-5">
                <SpecTable rows={facts.compositionRows} caption={`${product.name} composition`} />
              </div>
            </section>
          )}

          {facts.specifications.length > 0 && (
            <section className="mt-12" aria-labelledby="specifications-heading">
              <h2 id="specifications-heading" className="font-display text-h3 font-semibold text-ink">
                Technical specifications
              </h2>
              <div className="mt-4 rounded-xl border border-sand-200 bg-white px-5">
                <SpecTable rows={facts.specifications} caption={`${product.name} specifications`} />
              </div>
            </section>
          )}

          {(facts.dosageSummary || facts.dosageTable || facts.dosageNotes.length > 0) && (
            <section className="mt-12" aria-labelledby="dosage-heading">
              <h2 id="dosage-heading" className="font-display text-h3 font-semibold text-ink">
                Dosage &amp; application
              </h2>
              {facts.dosageSummary && <p className="mt-2 text-ink-600">{facts.dosageSummary}</p>}
              {facts.applicationMethods.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {facts.applicationMethods.map((m) => (
                    <span
                      key={m}
                      className="rounded-full border border-sand-200 bg-canvas px-3 py-1 text-sm text-ink-700"
                    >
                      {m}
                    </span>
                  ))}
                </div>
              )}
              {facts.dosageTable && (
                <div className="mt-5">
                  <DosageTable table={{ rows: facts.dosageTable.rows, caption: facts.dosageTable.caption }} />
                </div>
              )}
              {facts.dosageNotes.length > 0 && (
                <div className="mt-5 rounded-xl border border-sand-200 bg-white px-5">
                  <SpecTable rows={facts.dosageNotes} caption={`${product.name} dosage notes`} />
                </div>
              )}
              <p className="mt-3 text-xs text-ink-500">
                Doses are reproduced from the {product.name} technical specifications. Always follow the rate
                and timing printed on your pack label, and ask a crop advisor where field conditions differ.
              </p>
            </section>
          )}

          {facts.usage.length > 0 && (
            <section className="mt-12" aria-labelledby="usage-heading">
              <h2 id="usage-heading" className="font-display text-h3 font-semibold text-ink">
                How to use {product.name}
              </h2>
              <ol className="mt-5 space-y-3">
                {facts.usage.map((step, i) => (
                  <li key={step} className="flex gap-3 text-ink-700">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-600">
                      {i + 1}
                    </span>
                    <span className="leading-relaxed">{step}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {facts.precautions.length > 0 && (
            <section className="mt-12" aria-labelledby="precautions-heading">
              <h2 id="precautions-heading" className="font-display text-h3 font-semibold text-ink">
                Precautions &amp; safety
              </h2>
              <ul className="mt-5 space-y-2.5">
                {facts.precautions.map((line) => (
                  <li key={line} className="flex gap-3 text-ink-700">
                    <Icon name="AlertCircle" className="mt-0.5 h-4 w-4 shrink-0 text-clay" />
                    <span className="leading-relaxed">{line}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {(facts.packagingInfo || facts.shelfLife) && (
            <section className="mt-12" aria-labelledby="packaging-heading">
              <h2 id="packaging-heading" className="font-display text-h3 font-semibold text-ink">
                Packaging &amp; storage
              </h2>
              {facts.packagingInfo && <p className="mt-2 leading-relaxed text-ink-600">{facts.packagingInfo}</p>}
            </section>
          )}

          <ApprovalPanel product={product} licence={facts.licence} />

          {cropProgrammes.length > 0 && (
            <section className="mt-12" aria-labelledby="crop-programmes-heading">
              <h2 id="crop-programmes-heading" className="font-display text-h3 font-semibold text-ink">
                Crop programmes using {product.name}
              </h2>
              <p className="mt-2 text-ink-500">
                Each programme shows the stage-by-stage schedule documented for that crop.
              </p>
              <div className="mt-4 flex flex-wrap gap-2.5">
                {cropProgrammes.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/crop-solutions/${c.slug}`}
                    className="inline-flex items-center gap-2 rounded-full border border-sand-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 transition-colors hover:border-brand-400 hover:text-brand-700"
                  >
                    <Icon name="Sprout" className="h-4 w-4 text-brand-500" />
                    {product.name} for {c.name}
                  </Link>
                ))}
              </div>
            </section>
          )}

          <FaqBlock items={faq} heading={`${product.name} — questions and answers`} id="product-faq" />

          {articles.length > 0 && (
            <section className="mt-12" aria-labelledby="science-heading">
              <h2 id="science-heading" className="font-display text-h3 font-semibold text-ink">
                The science behind it
              </h2>
              <ul className="mt-4 space-y-2.5">
                {articles.map((a) => (
                  <li key={a.slug}>
                    <Link
                      to={`/knowledge/${a.slug}`}
                      className="inline-flex items-start gap-2.5 text-brand-700 hover:underline"
                    >
                      <Icon name="BookOpen" className="mt-1 h-4 w-4 shrink-0 text-brand-500" />
                      <span className="font-medium">{a.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {detailItems.length > 0 && (
            <div className="mt-12">
              <h2 className="mb-2 font-display text-h3 font-semibold text-ink">More product information</h2>
              <Accordion items={detailItems} />
            </div>
          )}
        </div>
      </div>

      <RelatedProducts products={related} />
    </div>
  );
}
